import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { useCreateReservation } from "@/hooks/reservations/useCreateReservation";
import { useAvailableTimeSlots } from "@/hooks/reservations/useAvailableTimeSlots";
import { useMonthAvailability } from "@/hooks/reservations/useMonthAvailability";
import { ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth } from "date-fns";

// Helper function to convert Date to local date string without timezone issues
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface ReservationCalendarProps {
  item: any;
  onBack: () => void;
}

export const ReservationCalendar = ({ item, onBack }: ReservationCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfMonth(new Date()));
  const [selectedTime, setSelectedTime] = useState<{start: string;end: string;} | null>(null);
  const createReservation = useCreateReservation();

  const selectedDateString = selectedDate ? toLocalDateString(selectedDate) : "";

  const { data: availableSlots, isLoading } = useAvailableTimeSlots(item.id, selectedDateString);
  const { data: monthAvailability } = useMonthAvailability(item.id, currentMonth);


  const handleReservation = async () => {
    if (!selectedDateString || !selectedTime) {
      toast.error("Selecciona una fecha y hora");
      return;
    }

    try {
      await createReservation.mutateAsync({
        item_id: item.id,
        reservation_date: selectedDateString,
        start_time: selectedTime.start,
        end_time: selectedTime.end,
        total_price: item.price_per_hour
      });
      onBack();
    } catch (error) {

      // Error is handled in the hook
    }};

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">Reservar: {item.name}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Item Info */}
        <Card>
          <CardHeader>
            <CardTitle>Detalles</CardTitle>
          </CardHeader>
          <CardContent>
            {item.image_url &&
            <div className="aspect-video rounded-lg overflow-hidden mb-4">
                <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover" />

              </div>
            }
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                
                <span>${item.price_per_hour}/hora</span>
              </div>
              {item.description &&
              <p className="text-sm text-muted-foreground">{item.description}</p>
              }
            </div>
          </CardContent>
        </Card>

        {/* Booking Interface */}
        <Card>
          <CardHeader>
            <CardTitle>Seleccionar fecha y hora</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Selection */}
            <div>
              <h4 className="font-medium mb-2">
                Selecciona una fecha
              </h4>
              <div className="border rounded-md overflow-hidden p-2 w-fit">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setSelectedTime(null); // Reset time selection when date changes
                  }}
                  onMonthChange={setCurrentMonth}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const targetDate = new Date(date);
                    targetDate.setHours(0, 0, 0, 0);

                    // Disable past dates
                    if (targetDate < today) return true;

                    // Disable dates without availability
                    const dateString = toLocalDateString(date);
                    const availability = monthAvailability?.[dateString];
                    return !availability?.hasAvailability;
                  }}
                  modifiers={{
                    available: (date) => {
                      const dateString = toLocalDateString(date);
                      return monthAvailability?.[dateString]?.hasAvailability || false;
                    }
                  }}
                  modifiersStyles={{
                    available: {
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      position: 'relative'
                    }
                  }}
                  className="w-full" />

              </div>
              {selectedDate && monthAvailability &&
              <p className="text-sm text-muted-foreground mt-2">
                  {monthAvailability[selectedDateString]?.slotCount || 0} horarios disponibles
                </p>
              }
            </div>

            {/* Time Selection */}
            {selectedDateString &&
            <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Horario
                </h4>
                {isLoading ?
              <div className="text-sm text-muted-foreground">Cargando horarios...</div> :
              availableSlots && availableSlots.length > 0 ?
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {availableSlots.map((slot) =>
                <Button
                  key={slot.id}
                  variant={
                  selectedTime?.start === slot.start_time ? "default" : "outline"
                  }
                  size="sm"
                  disabled={slot.is_reserved || !slot.available}
                  onClick={() => setSelectedTime({ start: slot.start_time, end: slot.end_time })}
                  className={slot.is_reserved || !slot.available ? "opacity-50" : ""}>

                        {slot.start_time} - {slot.end_time}
                        {slot.is_reserved &&
                  <span className="ml-1 text-xs">(Ocupado)</span>
                  }
                        {!slot.available && !slot.is_reserved &&
                  <span className="ml-1 text-xs">(No disponible)</span>
                  }
                      </Button>
                )}
                  </div> :

              <div className="text-sm text-muted-foreground">
                    No hay horarios disponibles para esta fecha
                  </div>
              }
              </div>
            }

            {/* Confirm Button */}
            {selectedDateString && selectedTime &&
            <div className="pt-4 border-t">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-medium">Total:</span>
                  <span className="text-lg font-bold">${item.price_per_hour}</span>
                </div>
                <Button
                onClick={handleReservation}
                disabled={createReservation.isPending}
                className="w-full">

                  {createReservation.isPending ? "Reservando..." : "Confirmar reserva"}
                </Button>
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </div>);

};